import {
    buildBreadcrumbManager,
    buildFacet,
    buildFieldSortCriterion,
    buildQuerySummary,
    buildRelevanceSortCriterion,
    buildResultList,
    buildSearchBox,
    buildSort,
    loadAdvancedSearchQueryActions,
    loadContextActions,
    loadFacetSetActions,
    loadPaginationActions,
    loadQueryActions,
    loadSearchActions,
    loadSearchAnalyticsActions,
    loadSortCriteriaActions,
    SortOrder,
} from '@coveo/headless';
import { useGeolocationManager } from 'contexts/GeolocationContext';
import { getHeadlessEngine } from 'libs/coveo/getHeadlessEngine';
import React, { useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { useSearchParams } from '@/hooks/useSearchParams';
import { trackInteraction } from '@/utils/analytics';
import { preserveHashInUrl } from '@/utils/preserveHashInUrl';

import isInMarketLocation from '../utils/isInMarketLocation';
import { useLocationSetStateContext } from './LocationStateContext';

// Helper function to extract URL parameters
const getSearchParamsWithGeolocation = ({ searchParams, currentGeolocation }) => {
    const urlParamQueryNames = [
        ...Object.keys(mapUrlParamsToQuery),
        ...Object.keys(mapUrlParamsToQueryDistance),
        ...Object.keys(mapUrlParamsToQueryZipCity),
        ...Object.keys(mapUrlParamsToSort),
        ...urlParamViewMode,
    ];

    const values = {};

    // Extract single values
    for (const name of urlParamQueryNames) {
        values[name] = searchParams.get(name);
    }

    // Extract multiple values
    for (const name of urlParamFilterNames) {
        values[name] = searchParams.getAll(name);
    }

    // Add geolocation fallback if not in URL
    if (!values['zip-code'] && !values['city'] && currentGeolocation) {
        if (
            currentGeolocation.zipCode &&
            currentGeolocation.zipCode === currentGeolocation.recentSearch
        ) {
            values['zip-code'] = currentGeolocation.zipCode;
        } else if (
            currentGeolocation.city &&
            currentGeolocation.city === currentGeolocation.recentSearch
        ) {
            values['city'] = currentGeolocation.city;
        }
    }

    return values;
};

const defaultValue = {
    coveo: {
        engine: null,
        controller: {
            searchBox: null,
            querySummary: null,
            resultList: null,
            sort: null,
        },
        action: {
            updateQuery: null,
            executeSearch: null,
        },
    },
    sortCriteria: null,
    handleSubmit: () => {},
    handleSingleFilter: () => {},
    handleMultipleFilters: () => {},
    handleFilterResetAll: () => {},
    handleShowResultList: () => {},
    handleShowMaps: () => {},
    triggerExecuteSearch: () => {},
    fetchAllMapResults: async () => [],
    noResultsComponent: null,
    errorComponent: null,
    vpcBannerComponent: null,
};

// Available Sort Types
const SORT_TYPES = {
    RELEVANCY: 'Relevancy',
    DISTANCE: 'Distance',
};

export const sortCriteria = [
    [SORT_TYPES.RELEVANCY, buildRelevanceSortCriterion()],
    [SORT_TYPES.DISTANCE, buildFieldSortCriterion('mydistanceinmiles', SortOrder.Ascending)],
    // TODO - need to be added on COVEO
    // ['Wait time', buildFieldSortCriterion('wait_time', SortOrder.Ascending)],
];

export const mapUrlParamsToQuery = {
    term: (v) => v,
    'open-now': (v) => {
        if (v !== 'true') {
            return '';
        }

        const now = new Date(new Date().toLocaleString('en', { timeZone: 'America/New_York' }));
        const weekday = [
            'sunday',
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
        ][now.getDay()];
        const nowInSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
        // const nowInSeconds = 23400; // 06:00:00
        // const nowInSeconds = 36000; // 10:00:00
        // const nowInSeconds = 43200; // 12:00:00
        // const nowInSeconds = 45000; // 12:30:00
        // const nowInSeconds = 46800; // 13:00:00
        // const nowInSeconds = 54000; // 15:00:00

        return [
            '(',
            `@locworkinghourssearch_${weekday}_closed = "False"`,
            `AND (@locworkinghourssearch_${weekday}_start <= ${nowInSeconds} AND @locworkinghourssearch_${weekday}_end >= ${nowInSeconds})`,
            `AND NOT (@locworkinghourssearch_${weekday}_breakstart < ${nowInSeconds} AND @locworkinghourssearch_${weekday}_breakend > ${nowInSeconds})`,
            ')',
        ].join(' ');
    },
};

export const mapUrlParamsToQueryDistance = {
    distance: (v) => (v && v > 0 ? `@mydistanceinmiles <= ${v}` : ''),
};

export const mapUrlParamsToQueryZipCity = {
    'zip-code': (v) => (v ? `@locationzip == ${v}` : ''),
    city: (v) => {
        const arr = v?.split(',');
        return v ? `@locationcity == "${arr[0]}" AND @locationstate == ${arr[arr.length - 1]}` : '';
    },
};

export const mapContextToQueryZipCity = {
    'zip-code': (v) => (v ? [{ contextKey: 'zip', contextValue: v }] : []),
    city: (v) => {
        const arr = v?.split(',');
        return v
            ? [
                  { contextKey: 'city', contextValue: arr[0] },
                  { contextKey: 'state', contextValue: arr[arr.length - 1] },
                  { contextKey: 'currentPage', contextValue: window.location.pathname },
              ]
            : [];
    },
};

export const mapUrlParamsToSort = {
    sort: (v) => sortCriteria.find(([name]) => name === v)?.[1] ?? sortCriteria[0][1],
};

export const urlParamFilterNames = ['type', 'specialty', 'condition', 'treatment', 'acc-ins'];

export const urlParamViewMode = ['view-mode'];

export const allUrlParamNames = [
    ...Object.keys(mapUrlParamsToQuery),
    ...Object.keys(mapUrlParamsToQueryDistance),
    ...Object.keys(mapUrlParamsToQueryZipCity),
    ...Object.keys(mapUrlParamsToSort),
    ...urlParamFilterNames,
    ...urlParamViewMode,
];

const removeUrlParams = (searchParams, exclude = []) => {
    const newSearchParams = new URLSearchParams(searchParams);

    const excludesEntries = [];
    for (const name of exclude) {
        const value = newSearchParams.getAll(name).filter(Boolean);
        if (!value || value.length === 0) {
            continue;
        }
        excludesEntries.push([name, value]);
    }

    for (const name of allUrlParamNames) {
        newSearchParams.delete(name);
    }

    for (const [name, value] of excludesEntries) {
        newSearchParams.append(name, value);
    }

    return newSearchParams;
};

export const LocationSearchContext = React.createContext(defaultValue);
export const LocationSearchZipCodeErrorContext = React.createContext(false);

export const LocationSearchProvider = (props) => {
    const {
        id,
        children,
        coveo,
        locationCategory,
        searchResultsDmParameters,
        noResultsComponent,
        errorComponent,
        vpcBannerComponent,
    } = props;

    const [zipCodeServerError, setZipCodeServerError] = React.useState(false);

    const coveoAnalyticsRef = useRef({ event: '', payload: {}, shouldReset: true });

    const history = useHistory();
    const searchParams = useSearchParams();

    const setState = useLocationSetStateContext();

    const { currentGeolocation, resolveGeolocation } = useGeolocationManager();
    const currentGeolocationRef = React.useRef(currentGeolocation);

    // Track the last search parameters to prevent infinite loops
    const lastSearchParamsRef = React.useRef(null);

    const engine = React.useMemo(
        () =>
            getHeadlessEngine(`location-search-${locationCategory || 'default'}-${id}`, {
                ...coveo,
                preprocessRequest: (request, _clientOrigin, metadata) => {
                    if (
                        !metadata ||
                        metadata.method !== 'search' ||
                        currentGeolocationRef.current === null
                    ) {
                        return request;
                    }

                    const body = JSON.parse(request.body);

                    body.queryFunctions = [
                        {
                            fieldName: '@mydistance',
                            function: `dist(@latitude, @longitude, ${
                                currentGeolocationRef.current.lat ?? 0
                            }, ${currentGeolocationRef.current.lng ?? 0})`,
                        },
                        {
                            fieldName: '@mydistanceinmiles',
                            function: '@mydistance*0.000621371',
                        },
                    ];

                    request.body = JSON.stringify(body);

                    return request;
                },
                analytics: { analyticsMode: 'legacy', trackingId: 'location-search' },
            }),
        [coveo, locationCategory, id, currentGeolocationRef],
    );

    const value = React.useMemo(() => {
        const AdvancedSearchQueryActions = loadAdvancedSearchQueryActions(engine);
        if (locationCategory) {
            engine.dispatch(
                AdvancedSearchQueryActions.updateAdvancedSearchQueries({
                    cq: `@loctype=${locationCategory}`,
                }),
            );
        }

        const QueryActionCreators = loadQueryActions(engine);
        const SortCriteriaActions = loadSortCriteriaActions(engine);
        const FacetSetActions = loadFacetSetActions(engine);
        const SearchAnalyticsActionCreators = loadSearchAnalyticsActions(engine);
        const SearchActionCreators = loadSearchActions(engine);
        const PaginationActions = loadPaginationActions(engine);
        const ContextActionCreators = loadContextActions(engine);

        const controllerSearchBox = buildSearchBox(engine, {
            options: { id: 'location-search-searchbox', numberOfSuggestions: 10 },
        });
        const controllerQuerySummary = buildQuerySummary(engine);
        const controllerResultList = buildResultList(engine, {
            options: {
                fieldsToInclude: [
                    'entitytype',
                    'loctype',
                    'locconditions',

                    // Distance
                    'mydistance',
                    'mydistanceinmiles',
                    'latitude',
                    'longitude',

                    // Open now
                    // 'locworkinghourssearch',

                    // Result List
                    'locname',
                    'locvariant',
                    'locimgurl',
                    'locparent',
                    'locbuildingname',
                    'locaddrline1',
                    'locaddrline2',
                    'locaddrline3',
                    'locationstate',
                    'locationzip',
                    'locmapurl',
                    'locphnum1',
                    'locworkinghours',
                    'locadjustedhours',
                    'locopen24hours',
                    'loctemporarilyclosed',
                    'locoverridemessage',
                    'locaddressoverridemessage',
                    'locwaittime',
                    'locepicdepartmentid',
                    'locroot',
                ],
            },
        });

        const hasZipOrCity =
            searchParams.get('zip-code') != null ||
            searchParams.get('city') != null ||
            (currentGeolocation && currentGeolocation?.recentSearch != null);
        const sortRelevancy = searchParams.get('sort') === SORT_TYPES.RELEVANCY;
        // If zip or city is set but sort is not set, set sort to Distance by default otherwise Relevancy
        const initialCriterion =
            hasZipOrCity && !sortRelevancy ? sortCriteria[1][1] : sortCriteria[0][1];
        const controllerSort = buildSort(engine, {
            initialState: { criterion: initialCriterion },
        });

        const factoryFacet = (facetId, field) => {
            return buildFacet(engine, {
                options: {
                    facetId,
                    field,
                    numberOfValues: 100,
                    resultsMustMatch: 'allValues',
                    // resultsMustMatch: 'atLeastOneValue',
                    sortCriteria: 'alphanumeric',
                },
            });
        };

        const controllerFacetType = factoryFacet('type', 'loctype');
        const controllerFacetSpecialty = factoryFacet('specialty', 'locspecialties');
        const controllerFacetCondition = factoryFacet('condition', 'loccondition');
        const controllerFacetTreatment = factoryFacet('treatment', 'locservices');
        // TODO - need to be added on COVEO
        const controllerFacetAcceptedInsurance = factoryFacet('acc-ins', 'locacceptedinsurance');

        engine.dispatch(FacetSetActions.updateFacetAutoSelection({ allow: true }));

        const controllerBreadcrumbManager = buildBreadcrumbManager(engine);

        const executeSearch = (event, payload = {}) => {
            const {
                logInterfaceLoad,
                logSearchboxSubmit,
                logResultsSort,
                logQuerySuggestionClick,
                logFacetSelect,
                logFacetDeselect,
                logFacetBreadcrumb,
                logClearBreadcrumbs,
                logStaticFilterSelect,
                logStaticFilterDeselect,
                logStaticFilterClearAll,
            } = SearchAnalyticsActionCreators;
            switch (event) {
                case 'resultsSort':
                    return SearchActionCreators.executeSearch(logResultsSort());
                case 'searchboxSubmit':
                    return SearchActionCreators.executeSearch(logSearchboxSubmit());
                case 'querySuggestionClick':
                    return SearchActionCreators.executeSearch(logQuerySuggestionClick(payload)); // LogQuerySuggestionClickActionCreatorPayload {id: string, suggestion: string}
                case 'facetSelect':
                    return SearchActionCreators.executeSearch(logFacetSelect(payload)); // LogFacetSelectActionCreatorPayload {facetId: string, facetValue: string}
                case 'facetDeselect':
                    return SearchActionCreators.executeSearch(logFacetDeselect(payload)); // LogFacetDeselectActionCreatorPayload {facetId: string, facetValue: string}
                case 'facetBreadcrumb':
                    return SearchActionCreators.executeSearch(logFacetBreadcrumb(payload)); // LogFacetBreadcrumbActionCreatorPayload {facetId: string, facetValue: string}
                case 'clearBreadcrumbs':
                    return SearchActionCreators.executeSearch(logClearBreadcrumbs());
                case 'staticFilterSelect':
                    return SearchActionCreators.executeSearch(logStaticFilterSelect(payload)); // LogStaticFilterToggleValueActionCreatorPayload {staticFilterId: string, staticFilterValue: StaticFilterValueMetadata}
                case 'staticFilterDeselect':
                    return SearchActionCreators.executeSearch(logStaticFilterDeselect(payload)); // LogStaticFilterToggleValueActionCreatorPayload {staticFilterId: string, staticFilterValue: StaticFilterValueMetadata}
                case 'staticFilterClearAll':
                    return SearchActionCreators.executeSearch(logStaticFilterClearAll(payload)); // LogStaticFilterClearAllActionCreatorPayload {staticFilterId: string}
                default:
                    return SearchActionCreators.executeSearch(logInterfaceLoad());
            }
        };

        const triggerExecuteSearch = async (urlParams) => {
            setState((prevState) => {
                if (prevState === 'default') {
                    coveoAnalyticsRef.current.shouldReset = false;
                }
                return 'results';
            });

            const { sort, 'zip-code': zipCodeParam, city: cityParam } = urlParams;

            setZipCodeServerError(false);

            // Update and get the latest geolocation
            const geolocation = await resolveGeolocation({
                zipCode: zipCodeParam,
                city: cityParam,
            });

            currentGeolocationRef.current = geolocation;

            if ((zipCodeParam || cityParam) && !geolocation) {
                setZipCodeServerError(true);
                return;
            }

            // Determine sort value based on presence of zip/city being set
            const shouldUseDistance = geolocation?.zipCode || geolocation?.city;
            const sortValue =
                sort || (shouldUseDistance ? SORT_TYPES.DISTANCE : SORT_TYPES.RELEVANCY);
            // Add context for zip, city, state
            let contextQueryArray = [];
            for (const name in mapContextToQueryZipCity) {
                if (Object.hasOwnProperty.call(mapContextToQueryZipCity, name)) {
                    const fn = mapContextToQueryZipCity[name];
                    contextQueryArray = [...contextQueryArray, ...fn(urlParams[name])];
                }
            }
            const contextQuery = contextQueryArray.filter(Boolean);
            const contextKeys = ['zip', 'city', 'state'];
            contextKeys.forEach((key) => {
                const found = contextQuery.find((query) => query?.contextKey === key);
                if (found) {
                    engine.dispatch(ContextActionCreators.addContext(found));
                } else {
                    engine.dispatch(ContextActionCreators.removeContext(key));
                }
            });

            const queryArray = [];
            for (const name in mapUrlParamsToQuery) {
                if (Object.hasOwnProperty.call(mapUrlParamsToQuery, name)) {
                    const fn = mapUrlParamsToQuery[name];
                    queryArray.push(fn(urlParams[name]));
                }
            }
            const query = queryArray
                .map((val) => (typeof val === 'string' ? val.trim() : val)) // trim each value to avoid leading/trailing spaces or empty values
                .filter(Boolean) // remove falsy values (e.g. empty strings)
                .join(' AND '); // join with AND operator
            const zipCityQueryArray = [];
            for (const name in mapUrlParamsToQueryZipCity) {
                if (Object.hasOwnProperty.call(mapUrlParamsToQueryZipCity, name)) {
                    const fn = mapUrlParamsToQueryZipCity[name];
                    zipCityQueryArray.push(fn(urlParams[name]));
                }
            }
            const zipCityQuery = zipCityQueryArray.filter(Boolean).join(' AND ');

            const distanceQueryArray = [];
            for (const name in mapUrlParamsToQueryDistance) {
                if (Object.hasOwnProperty.call(mapUrlParamsToQueryDistance, name)) {
                    const fn = mapUrlParamsToQueryDistance[name];
                    distanceQueryArray.push(fn(urlParams[name]));
                }
            }

            if (distanceQueryArray.filter(Boolean).length > 0) {
                distanceQueryArray.push(zipCityQuery);
            }

            const distanceQuery = distanceQueryArray
                .filter(Boolean)
                .map((q, index) => (index > 0 ? `(${q})` : q))
                .join(' OR ');

            // AHSMKTG-4096: Virtual Care is only available to patients physically in NJ, so
            // Virtual locations are suppressed once the searched location is known to be out
            // of state. An unknown location leaves them visible.
            // Single-`=` (match) is deliberate over `==` (exact): it also catches a future
            // compound type like `Virtual Visit`, which carries the same NJ constraint. See
            // docs/adr/0002-filter-virtual-locations-in-coveo-query.md.
            const virtualExclusionQuery = isInMarketLocation(geolocation)
                ? ''
                : 'NOT @loctype=virtual';

            // `distanceQuery` is OR-joined and Coveo binds AND tighter than OR, so it has to
            // be parenthesized before the exclusion is AND-ed onto it.
            let advancedQuery = distanceQuery || '';
            if (virtualExclusionQuery) {
                advancedQuery = advancedQuery
                    ? `(${advancedQuery}) AND ${virtualExclusionQuery}`
                    : virtualExclusionQuery;
            }

            const sortQuery = mapUrlParamsToSort.sort(sortValue);
            engine.dispatch(SortCriteriaActions.updateSortCriterion(sortQuery));

            for (const name of urlParamFilterNames) {
                engine.dispatch(FacetSetActions.deselectAllFacetValues(name));

                const values = urlParams[name];
                const filteredValues = values?.filter(Boolean);

                if (!filteredValues || filteredValues.length === 0) {
                    continue;
                }

                filteredValues.forEach((v) => {
                    const [value, numberOfResults] = v.split(';');

                    engine.dispatch(
                        FacetSetActions.toggleSelectFacetValue({
                            facetId: name,
                            selection: {
                                value,
                                numberOfResults: parseInt(numberOfResults, 20),
                                state: 'selected',
                            },
                        }),
                    );
                });
            }

            engine.dispatch(
                QueryActionCreators.updateQuery({
                    enableQuerySyntax: true,
                    q: `${query}`,
                }),
            );

            engine.dispatch(
                AdvancedSearchQueryActions.updateAdvancedSearchQueries({
                    aq: advancedQuery,
                }),
            );

            engine.dispatch(PaginationActions.updateNumberOfResults(20));
            engine.dispatch(
                executeSearch(
                    coveoAnalyticsRef?.current?.event,
                    coveoAnalyticsRef?.current?.payload,
                ),
            );
        };

        const setCoveoAnalytics = (event = '', payload = {}, shouldReset = true) => {
            coveoAnalyticsRef.current.event = event;
            coveoAnalyticsRef.current.payload = payload;
            coveoAnalyticsRef.current.shouldReset = shouldReset;
        };

        const handleSubmit = ({ term, zipCityValue, viewMode, analyticsCallback }) => {
            const currentTerm = searchParams.get('term');
            let newSearchParams = new URLSearchParams(history.location.search);

            const hasPreviousTerm = !!currentTerm;
            const hasCurrentTerm = !!term;

            // If term is changed, added, or removed all url params except view-mode
            if (
                ((!hasPreviousTerm && hasCurrentTerm) ||
                    (hasPreviousTerm && !hasCurrentTerm) ||
                    (hasPreviousTerm && hasCurrentTerm)) &&
                currentTerm !== term
            ) {
                newSearchParams = removeUrlParams(newSearchParams, ['view-mode']);
            }

            const values = [
                { name: 'term', value: term },
                { name: 'zip-code', value: !isNaN(zipCityValue) ? zipCityValue : null },
                { name: 'city', value: isNaN(zipCityValue) ? zipCityValue : null },
                { name: 'view-mode', value: viewMode },
                ...(zipCityValue ? [{ name: 'distance', value: '' }] : []),
                ...(zipCityValue
                    ? [{ name: 'sort', value: 'Distance' }]
                    : [{ name: 'sort', value: 'Relevancy' }]),
            ];

            values.forEach(({ name, value }) => {
                if (value) {
                    newSearchParams.set(name, value);
                } else {
                    newSearchParams.delete(name);
                }
            });

            const path = window.location.pathname;
            const newUrl = `${path}?${newSearchParams.toString()}`;

            const currentPath = window.location.pathname;
            const currentUrl = `${currentPath}${history.location.search}`;

            if (currentUrl === newUrl) {
                return false;
            }

            if (!analyticsCallback) {
                setCoveoAnalytics('searchboxSubmit', {}, false);
            } else {
                analyticsCallback();
            }

            history.push(preserveHashInUrl(newUrl));
        };

        const handleSingleFilter = (partial, analytics) => {
            const newSearchParams = new URLSearchParams(history.location.search);

            for (const name in partial) {
                if (Object.hasOwnProperty.call(partial, name)) {
                    const value = partial[name];

                    if (value) {
                        newSearchParams.set(name, value);
                    } else {
                        newSearchParams.delete(name);
                    }
                }
            }

            const path = window.location.pathname;
            const newUrl = `${path}?${newSearchParams.toString()}`;

            // analytics
            if (analytics) {
                trackInteraction({
                    componentName: 'Filter Sort Panel',
                    interactionType: 'filter',
                    selector: 'filter',
                    allFilters: newSearchParams,
                    ...analytics,
                });
            }

            history.push(preserveHashInUrl(newUrl));
        };

        const handleMultipleFilters = (partial, analytics) => {
            const newSearchParams = new URLSearchParams(history.location.search);

            for (const name in partial) {
                if (Object.hasOwnProperty.call(partial, name)) {
                    const value = partial[name];

                    if (newSearchParams.has(name, value)) {
                        newSearchParams.delete(name, value);
                    } else {
                        const params = newSearchParams.getAll(name);
                        const iname = value?.split(';')[0];
                        const matched = params?.find(
                            (p) => p?.includes(';0') !== -1 && p?.split(';')[0] === iname,
                        );
                        if (matched) {
                            newSearchParams.delete(name, matched);
                        } else {
                            newSearchParams.append(name, value);
                        }
                    }
                }
            }

            const path = window.location.pathname;
            const newUrl = `${path}?${newSearchParams.toString()}`;

            // analytics
            if (analytics) {
                trackInteraction({
                    componentName: 'Filter Sort Panel',
                    interactionType: 'filter',
                    selector: 'filter',
                    allFilters: newSearchParams,
                    ...analytics,
                });
            }
            history.push(preserveHashInUrl(newUrl));
        };

        const handleFilterResetAll = () => {
            const newSearchParams = removeUrlParams(new URLSearchParams(history.location.search), [
                'term',
                'zip-code',
                'city',
                'view-mode',
            ]);

            const path = window.location.pathname;
            const newUrl = `${path}?${newSearchParams.toString()}`;

            history.push(preserveHashInUrl(newUrl));
        };

        const handleShowResultList = () => {
            const searchParams = new URLSearchParams(history.location.search);

            searchParams.delete('view-mode');

            const path = window.location.pathname;
            const newUrl = `${path}?${searchParams.toString()}`;

            history.push(preserveHashInUrl(newUrl));
        };

        const handleShowMaps = () => {
            const searchParams = new URLSearchParams(history.location.search);

            searchParams.set('view-mode', 'maps');

            const path = window.location.pathname;
            const newUrl = `${path}?${searchParams.toString()}`;

            history.push(preserveHashInUrl(newUrl));
        };

        const handleOpenNow = (openNow) => {
            const searchParams = new URLSearchParams(history.location.search);

            if (openNow) {
                searchParams.set('open-now', 'true');
            } else {
                searchParams.delete('open-now');
            }

            const path = window.location.pathname;
            const newUrl = `${path}?${searchParams.toString()}`;

            history.push(preserveHashInUrl(newUrl));
        };

        /**
         * Fetches all results for map view by chunking requests.
         * This prevents "request too large" errors by fetching in batches.
         * @returns {Promise<Array>} All results from the search
         */
        const fetchAllMapResults = async () => {
            const CHUNK_SIZE = 100;
            const allResults = [];
            let firstResult = 0;
            let hasMoreResults = true;

            while (hasMoreResults) {
                try {
                    const queryState = engine.state.query;
                    const sortState = engine.state.sortCriteria;
                    const contextState = engine.state.context?.contextValues || {};
                    const facetState = engine.state.facetSet || {};

                    // Build selected facet values for the request
                    const facets = Object.entries(facetState)
                        .map(([facetId, facet]) => ({
                            facetId,
                            field: facet.request?.field,
                            currentValues:
                                facet.request?.currentValues?.filter(
                                    (v) => v.state === 'selected',
                                ) || [],
                        }))
                        .filter((f) => f.currentValues.length > 0);

                    const requestBody = {
                        q: queryState?.q || '',
                        aq: engine.state.advancedSearchQueries?.aq || '',
                        cq: engine.state.advancedSearchQueries?.cq || '',
                        firstResult,
                        numberOfResults: CHUNK_SIZE,
                        sortCriteria: sortState,
                        context: contextState,
                        facets: facets.map((f) => ({
                            facetId: f.facetId,
                            field: f.field,
                            currentValues: f.currentValues,
                        })),
                        fieldsToInclude: [
                            'entitytype',
                            'loctype',
                            'locconditions',
                            'mydistance',
                            'mydistanceinmiles',
                            'latitude',
                            'longitude',
                            'locname',
                            'locvariant',
                            'locimgurl',
                            'locparent',
                            'locbuildingname',
                            'locaddrline1',
                            'locaddrline2',
                            'locaddrline3',
                            'locationstate',
                            'locationzip',
                            'locmapurl',
                            'locphnum1',
                            'locworkinghours',
                            'locadjustedhours',
                            'locopen24hours',
                            'loctemporarilyclosed',
                            'locoverridemessage',
                            'locaddressoverridemessage',
                            'locwaittime',
                            'locepicdepartmentid',
                            'locroot',
                        ],
                    };

                    // Add query functions for distance calculation if we have geolocation
                    if (currentGeolocationRef.current) {
                        requestBody.queryFunctions = [
                            {
                                fieldName: '@mydistance',
                                function: `dist(@latitude, @longitude, ${
                                    currentGeolocationRef.current.lat ?? 0
                                }, ${currentGeolocationRef.current.lng ?? 0})`,
                            },
                            {
                                fieldName: '@mydistanceinmiles',
                                function: '@mydistance*0.000621371',
                            },
                        ];
                    }

                    const response = await fetch(`${coveo.platformUrl}/rest/search/v2`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${coveo.accessToken}`,
                        },
                        body: JSON.stringify(requestBody),
                    });

                    if (!response.ok) {
                        console.error('Failed to fetch map results chunk:', response.status);
                        break;
                    }

                    const data = await response.json();
                    const results = data.results || [];
                    allResults.push(...results);

                    // Check if there are more results to fetch
                    const totalCount = data.totalCount || 0;
                    firstResult += results.length;
                    hasMoreResults = results.length === CHUNK_SIZE && firstResult < totalCount;
                } catch (error) {
                    console.error('Error fetching map results:', error);
                    break;
                }
            }

            return allResults;
        };

        return {
            coveo: {
                engine,
                controller: {
                    searchBox: controllerSearchBox,
                    querySummary: controllerQuerySummary,
                    resultList: controllerResultList,
                    sort: controllerSort,
                    facet: {
                        type: controllerFacetType,
                        specialty: controllerFacetSpecialty,
                        condition: controllerFacetCondition,
                        treatment: controllerFacetTreatment,
                        acceptedInsurance: controllerFacetAcceptedInsurance,
                    },
                    breadcrumbManager: controllerBreadcrumbManager,
                },
                action: {
                    updateQuery: QueryActionCreators.updateQuery,
                    executeSearch,
                },
            },
            sortCriteria,
            handleSubmit,
            handleSingleFilter,
            handleMultipleFilters,
            handleFilterResetAll,
            handleShowResultList,
            handleShowMaps,
            handleOpenNow,
            triggerExecuteSearch,
            fetchAllMapResults,
            noResultsComponent,
            setCoveoAnalytics,
            errorComponent,
            vpcBannerComponent,
            searchResultsDmParameters,
        };
    }, [
        locationCategory,
        engine,
        searchParams,
        searchResultsDmParameters,
        noResultsComponent,
        errorComponent,
        vpcBannerComponent,
        setState,
        history,
        resolveGeolocation,
        currentGeolocation,
        currentGeolocationRef,
        coveo,
    ]);

    // Trigger search when URL parameters change
    React.useEffect(() => {
        const urlParams = getSearchParamsWithGeolocation({ searchParams, currentGeolocation });
        const hasLocation = urlParams['zip-code'] || urlParams['city'];

        // For embedded search (no locationCategory), require term, location, or at least one filter
        if (!locationCategory) {
            const hasFilters = urlParamFilterNames.some(
                (name) => urlParams[name]?.filter(Boolean).length > 0,
            );
            if (!urlParams.term && !hasLocation && !hasFilters) {
                return;
            }
        } else {
            // Remove term for category searches
            delete urlParams.term;
        }

        // Create a stable key to compare search parameters
        const searchKey = JSON.stringify({
            locationCategory,
            urlParams,
        });

        // Only trigger search if parameters have actually changed
        // This prevents infinite loops if the URL parameters are the same
        if (lastSearchParamsRef.current !== searchKey) {
            lastSearchParamsRef.current = searchKey;
            value.triggerExecuteSearch(urlParams);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationCategory, searchParams, currentGeolocation]);

    return (
        <LocationSearchContext.Provider value={value}>
            <LocationSearchZipCodeErrorContext.Provider value={zipCodeServerError}>
                {children}
            </LocationSearchZipCodeErrorContext.Provider>
        </LocationSearchContext.Provider>
    );
};

export const useLocationSearchContext = () => {
    const context = React.useContext(LocationSearchContext);

    if (context == null) {
        throw new Error(`'useLocationSearchContext' needs to be within 'LocationSearchContext'`);
    }

    return context;
};

export const useLocationSearchZipCodeErrorContext = () => {
    const context = React.useContext(LocationSearchZipCodeErrorContext);

    if (context == null) {
        throw new Error(
            `'useLocationSearchZipCodeErrorContext' needs to be within 'LocationSearchZipCodeErrorContext'`,
        );
    }

    return context;
};
